import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLocationDot,
    faChargingStation,
    faPlug,
    faClock,
    faUser,
    faAddressCard,
    faCircleCheck,
    faCircleXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
    StatusIconByKey,
    getStatusLabelWithMaintenance,
    getStatusColorStyle,
} from "../../helpers/StatusMapper";
import { formatSwedishTime } from "../../helpers/DateTime";
import { SESSION_STATUSES, stripSiteWords } from "./chargeMonitorHelpers";

const COL =
    "grid grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-2 gap-y-1.5 text-sm md:grid-cols-[max-content_1.25rem_minmax(0,1fr)]";
const LABEL =
    "hidden whitespace-nowrap text-left text-xs font-normal uppercase tracking-wide text-mute-500 md:block";

const ChargeMonitorConnectorInfo = ({ item }) => {
    const { t } = useTranslation();
    const data = item?.Data || {};
    const status = (data.Status || data.chargingConnectorStatus || "").toUpperCase();
    const maintenanceMode = !!(data.maintenanceMode ?? data.MaintenanceMode);
    const visualStatus = maintenanceMode ? "UNAVAILABLE" : status;
    const isOnline = !!data.isOnline;
    const statusColorStyle = getStatusColorStyle(visualStatus);
    const pointColorStyle = getStatusColorStyle(isOnline ? "ONLINE" : "OFFLINE");
    const isSession = SESSION_STATUSES.includes(status);

    return (
        <div className="grid gap-y-1.5 md:contents">
            <div className={`${COL} md:col-span-1`}>
                <span className={LABEL}>
                    {t("entity.site")}:
                </span>
                <span className="flex size-5 shrink-0 items-center justify-center">
                    <FontAwesomeIcon icon={faLocationDot} className="text-sky-500 text-sm" />
                </span>
                <span className="min-w-0 font-medium tracking-normal text-black">
                    {data.ChargingSiteName ?? "–"}
                </span>

                <span className={LABEL}>
                    {t("entity.charger")}:
                </span>
                <span className="flex size-5 shrink-0 items-center justify-center">
                    <FontAwesomeIcon icon={faChargingStation} className="text-kraft-500 text-sm" />
                </span>
                <span className="min-w-0 font-medium tracking-normal text-black">
                    {stripSiteWords(data.ChargingSiteName, data.chargingPointName)}
                </span>

                <span className={LABEL}>
                    {t("entity.connector")}:
                </span>
                <span className="flex size-5 shrink-0 items-center justify-center">
                    <FontAwesomeIcon icon={faPlug} className="text-mute-500 text-sm" />
                </span>
                <span className="min-w-0 font-medium tracking-normal text-black">
                    {stripSiteWords(data.ChargingSiteName, data.chargingConnectorName)}
                </span>

                {isSession && data.SessionStartTime && (
                    <>
                        <span className={LABEL}>
                            {t("chargeMonitor.started")}:
                        </span>
                        <span className="flex size-5 shrink-0 items-center justify-center">
                            <FontAwesomeIcon icon={faClock} className="text-mute-500 text-sm" />
                        </span>
                        <span className="min-w-0 font-medium tracking-normal text-black">
                            {formatSwedishTime(data.SessionStartTime)}
                        </span>
                    </>
                )}
            </div>

            <div className={`${COL} md:col-span-1`}>
                <div className="col-span-2 flex min-w-0 items-center gap-3 md:contents">
                    <span className={LABEL}>
                        {t("common.status")}:
                    </span>
                    <span className="flex min-w-0 flex-1 items-center gap-2 md:contents">
                        <span className="flex size-5 shrink-0 items-center justify-center">
                            <FontAwesomeIcon
                                icon={StatusIconByKey[visualStatus] ?? StatusIconByKey.DEFAULT}
                                className="text-sm"
                                style={statusColorStyle}
                            />
                        </span>
                        <span className="min-w-0 truncate font-medium" style={statusColorStyle}>
                            {getStatusLabelWithMaintenance(status, maintenanceMode, t)}
                        </span>
                    </span>

                    <span className={LABEL}>
                        {t("entity.point")}:
                    </span>
                    <span className="flex min-w-0 flex-1 items-center gap-2 md:contents">
                        <span className="flex size-5 shrink-0 items-center justify-center">
                            <FontAwesomeIcon
                                icon={isOnline ? faCircleCheck : faCircleXmark}
                                className="text-sm"
                                style={pointColorStyle}
                            />
                        </span>
                        <span className="min-w-0 truncate font-medium" style={pointColorStyle}>
                            {isOnline ? t("common.online") : t("common.offline")}
                        </span>
                    </span>
                </div>

                {isSession && data.SessionCustomerName && (
                    <>
                        <span className={LABEL}>
                            {t("entity.customer")}:
                        </span>
                        <span className="flex size-5 shrink-0 items-center justify-center">
                            <FontAwesomeIcon icon={faUser} className="text-mute-500 text-sm" />
                        </span>
                        <span className="min-w-0 font-medium text-black">
                            {data.SessionCustomerName ?? "–"}
                        </span>
                    </>
                )}

                {isSession && data.SessionTokenName && (
                    <>
                        <span className={LABEL}>
                            {t("entity.token")}:
                        </span>
                        <span className="flex size-5 shrink-0 items-center justify-center">
                            <FontAwesomeIcon icon={faAddressCard} className="text-mute-500 text-sm" />
                        </span>
                        <span className="min-w-0 font-medium text-black">
                            {data.SessionTokenName ?? "–"}
                        </span>
                    </>
                )}

                {status === "RESERVED" && data.CurrentReservationExpiryTime && (
                    <>
                        <span className={`${LABEL} leading-tight`}>
                            {t("chargeMonitor.reservedUntil")}:
                        </span>
                        <span className="flex size-5 shrink-0 items-center justify-center">
                            <FontAwesomeIcon icon={faClock} className="text-mute-500 text-sm" />
                        </span>
                        <span className="min-w-0 font-medium tabular-nums text-black">
                            {formatSwedishTime(data.CurrentReservationExpiryTime)}
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};

export default ChargeMonitorConnectorInfo;
